import React from 'react'
import { Avatar, AvatarInfo } from '../../components/avatar'
import {
  IconArchived,
  IconEmail,
  IconEmailText,
  IconGear,
  IconHidden,
  IconPaperclip,
  IconPhone,
  IconReply,
  IconTeacherOnly,
  IconText,
  IconTimes,
  IconWarningCircle,
} from '../../icons/library'
import { DateWrapper } from '../../components/simple/date'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { RemoteHTMLTooltip, RemoteTooltip, Tooltip } from '../../components/simple/tooltip'
import { IconAnnouncementCall } from '../../icons/announcementCall'
import { MessageTags, TagsInfo } from './messageTags'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Swipeable } from '../../layouts/components/swipeable'
import { InboxTab } from './inboxes'
import { MiniPie, MiniPieData } from '../../components/charts/miniPie'
import { DeliveryStatus, MessageInfoLauncher } from '../modals/messageinfo/messageInfo'
import { PostableMessage } from './compose/send'
import { IconOnlyCheckbox } from '../../components/form/checkbox'

type UndeliveredCircleData = {
  red: boolean
  info?: string
  undelivered?: boolean
}

export type MessageData = {
  id: number
  type: 'email' | 'both' | 'system' | 'mass' | 'text' | 'phone' | 'attendance'
  avatar: AvatarInfo
  tags: TagsInfo
  threadSize: number
  lk?: { language: string; recipients: number }
  schoolOrDistrictLabel?: { text: string; tooltip: string }
  read: boolean
  canRead: boolean
  repliedTo: boolean
  date: string
  archived: boolean
  teacherOnly: boolean
  conference: boolean
  whatsapp: boolean
  content: string
  subject?: string
  phoneLength?: string
  hasAttachments: boolean
  district?: string
  attendancePie?: MiniPieData
  undeliveredCircle?: UndeliveredCircleData
  confirmReceiptPie?: MiniPieData
  sendersPerspective: boolean
  draft: boolean
  scheduled: boolean
  draftRequest?: PostableMessage
  canViewMessageInfo: boolean
  notYourMessage?: string
}
interface MessageProps {
  message: MessageData
  selected: boolean
  onSelect: (message: MessageData) => void
  tab: InboxTab
  reloadMessages?: () => void
  canSwipeToArchive?: boolean
  multiSelected?: boolean
  setMultiSelected?: (selected: boolean, shiftPressed: boolean) => void
  overrideRead?: boolean
  disabled?: boolean
}

export const MessageIcon: React.FC<{ message: MessageData }> = ({ message }) => {
  if (message.archived) return <IconHidden size="xl" />
  if (message.teacherOnly) return <IconTeacherOnly size="xl" />
  if (message.type === 'email') return <IconEmail className="text-email" size="xl" />
  if (message.type === 'text') return <IconText className="text-text" size="xl" />
  if (message.type === 'phone') return <IconPhone className="text-phone" size="xl" />
  if (message.type === 'both') return <IconEmailText className="text-both" size="xl" />
  if (message.type === 'mass') return <IconAnnouncementCall className="text-both" size="xl" />
  if (message.type === 'system') return <IconGear size="xl" />
  return null // attendance doesn't have an icon
}
//extends swipable props and passes them through
interface MessageBaseProps extends Omit<React.ComponentProps<typeof Swipeable>, 'children'> {
  avatar: AvatarInfo
  top: React.ReactNode
  bottom: React.ReactNode
  extra?: React.ReactNode
  blueEdge?: boolean
  selected?: boolean
  read?: boolean
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  checked?: boolean
  onChecked?: (checked: boolean, shiftPressed: boolean) => void
}

export const MessageBase: React.FC<MessageBaseProps> = ({
  avatar,
  top,
  bottom,
  blueEdge,
  onClick,
  extra,
  selected,
  read,
  checked,
  onChecked,
  className,
  ...props
}) => {
  const bs = 'hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3),_0_1px_3px_1px_rgba(60,64,67,0.15)]'
  return (
    <Swipeable
      className={`messagebase cursor-pointer hover:z-10 ${bs} ${selected ? 'bg-slate-100' : read ? 'bg-slate-50' : 'bg-white'} ${className}`}
      onClick={onClick}
      {...props}
    >
      <div className="flex min-h-[60px] items-center border-b border-slate-200">
        {(onChecked || checked) && (
          <IconOnlyCheckbox
            checked={checked || false}
            onChecked={onChecked}
            disabled={!onChecked}
            size="lg"
            solid
            className={`ml-2 mr-1 ${checked ? 'text-blue-600 hover:text-slate-400' : 'text-slate-300 hover:text-blue-600'}`}
          />
        )}
        <Avatar src={avatar} className="ml-1" />
        <div className={`flex flex-grow flex-col gap-1 overflow-hidden px-2 py-2`}>
          <div className="flex items-center gap-2 overflow-hidden">{top}</div>
          {bottom && <div className="flex items-center gap-2 overflow-hidden">{bottom}</div>}
          {extra && <div className="flex items-center gap-2 overflow-hidden">{extra}</div>}
        </div>
        {blueEdge && <div className="-mb-[1px] self-stretch border-r-2 border-link"></div>}
      </div>
    </Swipeable>
  )
}

export const Message: React.FC<MessageProps> = ({
  message,
  onSelect,
  selected,
  tab,
  reloadMessages = () => {},
  canSwipeToArchive,
  multiSelected,
  setMultiSelected,
  overrideRead,
  disabled,
}) => {
  const [loading, setLoading] = React.useState(false)
  const read = overrideRead ?? message.read
  return (
    <MessageBase
      onClick={() => onSelect(message)}
      {...(canSwipeToArchive
        ? {
            onSwipeLeft: () => {
              setTimeout(() => toggleMessageDeletion(message.id, tab === InboxTab.Archived, reloadMessages), 150)
              return true
            },
            swipeLeftIndicator:
              tab === InboxTab.Archived ? (
                <div className="flex h-full w-full items-center justify-center bg-green-500 text-white">Restore</div>
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-logo text-white">Archive</div>
              ),
          }
        : {})}
      className={disabled || loading ? 'pointer-events-none opacity-50' : ''}
      read={read}
      selected={selected}
      avatar={message.avatar}
      checked={multiSelected}
      onChecked={setMultiSelected}
      top={
        <>
          <MessageTags
            tags={message.tags}
            read={read}
            extras={
              <>
                {message.threadSize > 1 ? (
                  <Badge>{message.threadSize}</Badge>
                ) : (
                  message.lk && message.lk.recipients > 1 && <Badge>{message.lk.recipients}</Badge>
                )}
                {message.schoolOrDistrictLabel && (
                  <Badge badgeStyle={BadgeStyle.mint} tooltip={message.schoolOrDistrictLabel.tooltip}>
                    {message.schoolOrDistrictLabel.text}
                  </Badge>
                )}
                {message.undeliveredCircle && (
                  <UndeliveredCircle
                    messageID={message.id}
                    undeliveredCircle={message.undeliveredCircle}
                    canViewMessageInfo={message.canViewMessageInfo}
                  />
                )}
                {message.notYourMessage && (
                  <Badge tooltip="You are viewing someone else's message" badgeStyle={BadgeStyle.blue}>
                    {message.notYourMessage}
                  </Badge>
                )}
                {message.attendancePie && <MiniPie size={32} data={message.attendancePie} className="ml-2" />}
              </>
            }
          />
          {message.repliedTo && <IconReply className="mr-1 opacity-60" />}
          <DateWrapper
            date={message.date}
            relative={message.type !== 'attendance'}
            detailed={message.type === 'attendance'}
            className={`flex-shrink-0 opacity-60 ${read ? '' : 'font-semibold'}`}
          />
          {message.draft && (
            <IconTimes
              className="cursor-pointer"
              tooltip={
                !message.scheduled
                  ? 'Delete Draft'
                  : message.type === 'phone'
                    ? 'Cancel this scheduled call'
                    : 'Cancel sending and move to drafts'
              }
              onClick={(e) => {
                setLoading(true)
                makeRequest('/deleteDraft', { id: message.id })
                  .then(reloadMessages)
                  .catch((e) => {
                    showAxiosErrorAsToast(e)
                    setLoading(false)
                  })
                // dont turn off loading, since the message will be removed on reload
                e.stopPropagation()
              }}
            />
          )}
        </>
      }
      bottom={
        message.type === 'attendance' ? undefined : (
          <>
            <MessageInfoLauncher messageID={message.id} canLaunch={message.canViewMessageInfo}>
              <MessageIcon message={message} />
            </MessageInfoLauncher>
            <div className="overflow-hidden text-ellipsis text-nowrap">{message.subject}</div>
            <div
              className="flex-shrink-[10000] overflow-hidden text-ellipsis text-nowrap opacity-75"
              dangerouslySetInnerHTML={{ __html: message.content }}
            ></div>
            <div className="ml-auto flex items-center gap-2">
              {message.hasAttachments && <IconPaperclip className="opacity-60" />}
              {message.confirmReceiptPie && (
                <ConfirmReceiptPie
                  pie={message.confirmReceiptPie}
                  messageID={message.id}
                  canViewMessageInfo={message.canViewMessageInfo}
                />
              )}
            </div>
          </>
        )
      }
      blueEdge={!read}
    />
  )
}

const UndeliveredCircle: React.FC<{
  undeliveredCircle: UndeliveredCircleData
  messageID: number
  canViewMessageInfo: boolean
}> = ({ undeliveredCircle, messageID, canViewMessageInfo }) => (
  <MessageInfoLauncher
    messageID={messageID}
    canLaunch={canViewMessageInfo}
    initialFilter={{
      deliveryStatus: undeliveredCircle.undelivered ? DeliveryStatus.failed : DeliveryStatus.unreachable,
    }}
  >
    {undeliveredCircle.info ? (
      <Tooltip text={undeliveredCircle.info}>
        <IconWarningCircle className={undeliveredCircle.red ? 'text-red-500' : 'text-warning'} />
      </Tooltip>
    ) : (
      <RemoteHTMLTooltip
        loadFrom={{
          url: '/messageUndeliveredInfo',
          data: { id: messageID },
        }}
      >
        <IconWarningCircle className={undeliveredCircle.red ? 'text-red-500' : 'text-warning'} />
      </RemoteHTMLTooltip>
    )}
  </MessageInfoLauncher>
)

export const ConfirmReceiptPie: React.FC<{ pie: MiniPieData; messageID: number; canViewMessageInfo: boolean }> = ({
  pie,
  messageID,
  canViewMessageInfo,
}) => (
  <MessageInfoLauncher messageID={messageID} canLaunch={canViewMessageInfo}>
    <RemoteTooltip<{ read: number; total: number; users: string[] }>
      loadFrom={{ url: '/frontend_api/getMessageIsReadTooltip', data: { id: messageID } }}
      processResult={(result) => (
        <>
          <div className="mb-1 text-lg font-bold">
            Read by {result.read} / {result.total} recipients
          </div>
          {result.read > 0 && <b>This message has been read by:</b>}
          {result.users.map((name, i) => (
            <div key={i}>{name}</div>
          ))}
          {result.users.length < result.read && <b>And {result.read - result.users.length} more</b>}
        </>
      )}
    >
      <MiniPie data={pie} />
    </RemoteTooltip>
  </MessageInfoLauncher>
)

export const toggleMessageDeletion = (id: number, restore: boolean, then: () => void) => {
  makeRequest('/deleteMessage', { id, restore }).then(then).catch(showAxiosErrorAsToast)
}
